import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSession } from "../hooks";
import { Api, ApiError } from "../sync/api";
import type { ReauthInfo } from "../sync/types";
import { cancelled, credentialJSON, passkeysHere, requestOptions } from "../webauthn";
import { Field, Form, Sheet } from "./ui";
import { t } from "../i18n";

type Method = ReauthInfo["methods"][number];

// Reauth asks the signed-in person to confirm it is them when the server
// wants it before an admin change or another sensitive action (ADR-0026),
// with whatever they sign in with; the refused request then goes again.
// Requests refused together wait on the one question.
export function Reauth() {
  const [answer, setAnswer] = useState<((ok: boolean) => void) | null>(null);
  const waiting = useRef<Promise<boolean> | null>(null);
  useEffect(() => {
    Api.onReauth = () =>
      (waiting.current ??= new Promise<boolean>((resolve) =>
        setAnswer(() => (ok: boolean) => {
          waiting.current = null;
          setAnswer(null);
          resolve(ok);
        }),
      ));
    return () => {
      Api.onReauth = undefined;
    };
  }, []);
  return answer && <ReauthSheet onDone={answer} />;
}

const methodNames = (): Record<Method, string> => ({
  password: t("Password"),
  code: t("Authenticator or recovery code"),
  passkey: t("Passkey"),
  sso: t("Single sign-on"),
});

function ReauthSheet({ onDone }: { onDone: (ok: boolean) => void }) {
  const { api } = useSession();
  const [info, setInfo] = useState<ReauthInfo | null>(null);
  const [method, setMethod] = useState<Method | "">("");
  const [secret, setSecret] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      const got = await api.reauthMethods();
      // A passkey works only at the server's own address, in a browser
      // that has them.
      if (got.methods.includes("passkey")) {
        const m = await api.request<{ passkeys?: { origin: string } }>("GET", "/auth/methods").catch(() => undefined);
        if (!passkeysHere(m)) got.methods = got.methods.filter((x) => x !== "passkey");
      }
      if (!live) return;
      setInfo(got);
      setMethod(got.methods[0] ?? "");
    })().catch((err) => live && setError(err instanceof ApiError ? err.message : String(err)));
    return () => {
      live = false;
    };
  }, [api]);

  const failed = (err: unknown) => setError(err instanceof ApiError ? err.message : String(err));

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setError("");
    try {
      await api.reauth(method === "code" ? { code: secret.trim() } : { password: secret });
      onDone(true);
    } catch (err) {
      failed(err);
    }
  }

  async function passkey() {
    setBusy(true);
    setError("");
    try {
      const begun = await api.beginReauthPasskey();
      const got = (await navigator.credentials.get({ publicKey: requestOptions(begun.options) })) as PublicKeyCredential | null;
      if (!got) return;
      await api.finishReauthPasskey(begun.pending, credentialJSON(got));
      onDone(true);
    } catch (err) {
      if (!cancelled(err)) failed(err);
    } finally {
      setBusy(false);
    }
  }

  // sso signs the person in again at their identity provider, in a window
  // of its own that says how it went and closes; if it cannot open, the
  // whole page goes, and the change is made again after.
  async function sso() {
    setBusy(true);
    setError("");
    try {
      const { url } = await api.reauthSSO();
      const popup = window.open(url, "lantern-reauth", "popup,width=520,height=680");
      if (!popup) {
        location.assign(url);
        return;
      }
      const said = await new Promise<{ ok: boolean; error?: string } | null>((resolve) => {
        const heard = (e: MessageEvent) => {
          if (e.origin !== location.origin || e.data?.lantern !== "reauth") return;
          end({ ok: !!e.data.ok, error: e.data.error });
        };
        const timer = setInterval(() => popup.closed && end(null), 500);
        const end = (r: { ok: boolean; error?: string } | null) => {
          clearInterval(timer);
          removeEventListener("message", heard);
          resolve(r);
        };
        addEventListener("message", heard);
      });
      if (said?.ok) return onDone(true);
      if (said?.error) return setError(t("Your identity provider did not confirm it was you."));
      // Closed without a word: the session may still have been confirmed.
      const now = await api.reauthMethods();
      if (new Date(now.until).getTime() > Date.now()) onDone(true);
    } catch (err) {
      failed(err);
    } finally {
      setBusy(false);
    }
  }

  const names = methodNames();
  return (
    <Sheet title={t("Confirm it's you")} onClose={() => onDone(false)} className="page reauth-sheet" size="sm">
      <p className="muted">
        {info
          ? t("Admin changes and some changes to your account need you to confirm it's you. It then lasts {n} minutes.", { n: info.minutes })
          : t("Admin changes and some changes to your account need you to confirm it's you.")}
      </p>
      {info && info.methods.length === 0 && <p className="error">{t("This account has no way to confirm it's you here. Ask an admin.")}</p>}
      {info && info.methods.length > 1 && (
        <div className="reauth-methods" role="group" aria-label={t("Confirm with")}>
          {info.methods.map((m) => (
            <button key={m} type="button" className={`small ${m === method ? "primary" : "ghost"}`} aria-pressed={m === method} onClick={() => (setMethod(m), setSecret(""), setError(""))}>
              {names[m]}
            </button>
          ))}
        </div>
      )}
      {(method === "password" || method === "code") && (
        <Form onSubmit={confirm} aria-label={t("Confirm it's you")}>
          <Field label={method === "password" ? t("Your password") : t("Code from your authenticator, or a recovery code")}>
            {method === "password" ? (
              <input type="password" autoComplete="current-password" value={secret} onChange={(e) => setSecret(e.target.value)} required data-autofocus />
            ) : (
              <input autoComplete="one-time-code" value={secret} onChange={(e) => setSecret(e.target.value)} required data-autofocus />
            )}
          </Field>
          {error && <p className="error">{error}</p>}
          <div className="form-actions">
            <button type="submit">{t("Confirm")}</button>
          </div>
        </Form>
      )}
      {(method === "passkey" || method === "sso") && (
        <>
          {error && <p className="error">{error}</p>}
          <div className="form-actions">
            <button type="button" disabled={busy} onClick={() => void (method === "passkey" ? passkey() : sso())} data-autofocus>
              {method === "passkey" ? t("Use a passkey") : t("Sign in again")}
            </button>
          </div>
        </>
      )}
      {!method && error && <p className="error">{error}</p>}
    </Sheet>
  );
}
